/**
 * Things lying about inside buildings (items with `loot.indoor`): rolled once per building per
 * game day, the first time somebody walks in, on a floor cell beside the furniture. Nobody's
 * house (a player's property) gets any — what is in there is the owner's stash.
 */
import { CELL_FLOOR, CELL_FURN, floorPlan, type InteriorKit } from '../world/interior';
import { hash, Rng } from '../world/rng';
import { ZONES } from '../world/generator';
import type { ZoneName } from '../content/types';
import { newId } from './entities';
import type { Game } from './game';

const rolledBy = new WeakMap<Game, Map<string, number>>();
function rolledOf(game: Game): Map<string, number> { let m = rolledBy.get(game); if (!m) { m = new Map(); rolledBy.set(game, m); } return m; }

/** The chance of an item lying in a building of this kit. */
export function indoorChance(indoor: number | Partial<Record<InteriorKit, number>> | undefined, kit: InteriorKit): number {
  if (indoor === undefined) return 0;
  return typeof indoor === 'number' ? indoor : indoor[kit] ?? 0;
}

export function indoorLoot(game: Game, key: string): void {
  const rolled = rolledOf(game);
  if (rolled.get(key) === game.day) return;
  rolled.set(key, game.day);
  if (game.props.has(key)) return;
  const [bx, by] = key.split(',').map(Number);
  const b = game.world.buildingAt(bx + 0.5, by + 0.5); if (!b) return;
  const plan = floorPlan(b, 0, game.seed); if (!plan) return;
  const rng = new Rng(hash(game.seed, bx, by, game.day, 77));
  const spots: [number, number][] = [];
  for (let ly = 1; ly < plan.h - 1; ly++) for (let lx = 1; lx < plan.w - 1; lx++) {
    if (plan.cells[ly * plan.w + lx] !== CELL_FLOOR) continue;
    const furn = [[1, 0], [-1, 0], [0, 1], [0, -1]].some(([dx, dy]) => plan.cells[(ly + dy) * plan.w + lx + dx] === CELL_FURN);
    if (furn) spots.push([plan.x + lx + 0.5, plan.y + ly + 0.5]);
  }
  if (!spots.length) return;
  const zone = ZONES[game.world.zoneAt(b.x + b.w / 2, b.y + b.h / 2)] as ZoneName;
  const items: Record<string, number> = {};
  for (const it of game.reg.all('item')) { if (rng.chance(indoorChance(it.loot?.indoor, plan.kit))) items[it.id] = (items[it.id] ?? 0) + 1; }
  // some change down the back of the sofa
  if (rng.chance(0.25)) items.cash = Math.max(1, Math.round((game.reg.economy.loot.zone[zone] ?? 10) * (0.1 + rng.float() * 0.4)));
  if (!Object.keys(items).length) return;
  const [x, y] = rng.pick(spots);
  game.pickups.push({ id: newId(), x, y, weaponId: 'items', ammo: 0, home: 'indoor', t: game.time, items, inside: key, floor: 0 });
}
